import Layout from 'components/layout';

import { i18nFunct } from '@lucid/gatsby-theme-lucid';
import { graphql } from 'gatsby';
import debounce from 'lodash.debounce';
import {
  Box,
  Card,
  Heading,
  Icon,
  IconType,
  VideoPlayer
} from 'lucid-components-react';
import * as queryString from 'query-string';
import React, { MouseEvent, useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';

import Footer from 'components/footer/footer';
import Navbar from 'components/navbar/navbar';
import AdditionalResources from 'components/additional-resources/additional-resources';
import Link from 'components/link/link';
import LayoutContext from 'contexts/layoutContext';
import {
  Drupal_FieldNodeCaseStudyHubFieldAdditionalResources,
  Drupal_FieldNodeCaseStudyHubFieldAdditionalResourcesCtas,
  Drupal_FieldNodeCaseStudyHubFieldHeroCta,
  Drupal_Metatag,
  Drupal_NodeCaseStudy,
  Drupal_NodeCaseStudyHub,
  Drupal_ParagraphAdditionalResources,
  Drupal_ParagraphCtaButton,
  Drupal_ParagraphMarketoForm,
  Drupal_TaxonomyTerm
} from 'generated/graphql';
import useGetPhoneNumber from 'hooks/use-get-phone-number';
import useMarketoLink from 'hooks/use-marketo-link';
import { useMenuDefaultFooter, useMenuTopNavigation } from 'hooks/use-menu';
import { Text } from 'theme/typography';
import { TemplateProps, LucidTheme } from 'types/types';
import { getEntityFields } from 'utils/getEntityFields';
import CaseStudySelect from './case-study-select';

interface Filter {
  value: string;
  label: string;
}

const blurbButtonCss = (theme: LucidTheme) => ({
  marginBottom: theme.space[3],
  marginRight: theme.space[5],
  '&:last-of-type': {
    marginRight: theme.space[0]
  }
});

const filterCss = (theme: LucidTheme) => ({
  flex: '0 1 30%',
  [`@media only screen and (max-width: ${theme.breakpoints.tabletSmall})`]: {
    flex: '0 1 100%',
    marginBottom: theme.space[6],
    '&:last-child': {
      marginBottom: theme.space[0]
    }
  }
});

const CaseStudyHub = ({ data, pageContext, location }: TemplateProps) => {
  const { drupal } = data;
  const node = drupal.nodeById as Drupal_NodeCaseStudyHub;
  const allTiles = drupal.nodeQuery!.entities as Drupal_NodeCaseStudy[];
  const filters = drupal.taxonomyTermQuery!.entities as Drupal_TaxonomyTerm[];
  const { i18nData } = data;

  const {
    i18nKey,
    page = 1,
    pageSize = 9,
    hubAlias = 'case-studies',
    hideSsr
  } = pageContext;
  const i18n = i18nFunct(i18nData, i18nKey);

  const filterGroups: Record<string, Array<Filter>> = {
    industry: [{ value: '', label: i18n('mktg_ct_cs_placeholder_1') }],
    size: [{ value: '', label: i18n('mktg_ct_cs_placeholder_2') }],
    role: [{ value: '', label: i18n('mktg_ct_cs_placeholder_3') }]
  };

  filters.forEach(filter => {
    const key = filter.entityBundle as keyof typeof filterGroups;
    const name = filter.name;
    const label = filter.entityTranslation
      ? filter.entityTranslation.entityLabel
      : name;
    if (name && label) {
      filterGroups[key].push({ value: name, label });
    }
  });

  const {
    industry: queryIndustry,
    size: querySize,
    role: queryRole,
    search: querySearch,
    page: queryPage
  } = queryString.parse(location.search) as Record<string, string>;

  const pagedAllTiles = allTiles.slice((page - 1) * pageSize);
  const [tiles, setTiles] = useState(pagedAllTiles);
  const [search, setSearch] = useState(querySearch);
  const [industry, setIndustry] = useState(queryIndustry);
  const [size, setSize] = useState(querySize);
  const [role, setRole] = useState(queryRole);

  const viewPage = queryPage ? parseInt(queryPage, 10) : page;
  const [currentPage, setCurrentPage] = useState(viewPage || 1);

  const handleSearch = debounce((value: string) => {
    setSearch(value);
  }, 150);

  const viewMore = (e: MouseEvent) => {
    e.preventDefault();
    const newCurrentPage = currentPage + 1;
    setCurrentPage(newCurrentPage);
  };

  const totalPageCount = Math.ceil(tiles.length / pageSize);
  const hasMoreWebinars = currentPage < totalPageCount;

  const filterTiles = () => {
    const searchFilters: Record<string, string> = {
      search,
      industry,
      size,
      role
    };

    const newQueryParams: Record<string, string | number> = {};

    const filterValues = Object.entries(searchFilters)
      .filter(entry => entry[1])
      .map(entry => {
        newQueryParams[entry[0]] = entry[1];
        return entry[1].toLowerCase();
      });

    if (currentPage > 1) {
      newQueryParams.page = currentPage;
    }

    const queryParams = queryString.stringify(newQueryParams);
    window.history.replaceState(
      {},
      '',
      `${location.pathname}${queryParams ? '?' : ''}${queryParams}`
    );

    const filteredTiles = (currentPage <= 1 ? allTiles : pagedAllTiles).filter(
      tile => {
        if (filterValues.length < 1) {
          return true;
        }

        const tileValues = [
          ...(tile.fieldCompanyName?.toLowerCase().split(' ') || ''),
          tile.fieldSize?.entity?.name?.toLowerCase(),
          ...(tile.fieldRole
            ?.filter(role => role?.entity?.name)
            .map(role => {
              const entity = role?.entity as Drupal_TaxonomyTerm;
              return entity.name?.toLowerCase();
            }) || ''),
          ...(tile.fieldIndustry
            ?.filter(industry => industry?.entity?.name)
            .map(industry => {
              const entity = industry?.entity as Drupal_TaxonomyTerm;
              return entity.name?.toLowerCase();
            }) || ''),
          ...(tile.fieldSearchTerms
            ?.filter(term => term?.entity?.name)
            .map(term => term?.entity?.name?.toLowerCase()) || '')
        ];

        const matches = filterValues.filter(
          filterValue =>
            tileValues.filter(tileValue => tileValue?.includes(filterValue))
              .length
        );

        return filterValues.length === matches.length;
      }
    );

    setTiles(filteredTiles);
  };

  useEffect(() => {
    filterTiles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, industry, size, role, currentPage]);

  const { nid, langPrefix, pageType } = pageContext;

  const { entityMetatags, entityTranslations } = getEntityFields(node);
  const titleMetatag = (entityMetatags as Drupal_Metatag[]).find(
    ({ key }) => key === 'title'
  );
  const title = titleMetatag ? (
    <title key={titleMetatag.key}>
      {`Page ${currentPage} for ${titleMetatag.value}`}
    </title>
  ) : null;

  const navbarMenu = useMenuTopNavigation(langPrefix);
  const footerMenu = useMenuDefaultFooter(langPrefix);

  const getMarketoLink = useMarketoLink({
    i18nKey,
    langPrefix
  });

  const phoneNumber = useGetPhoneNumber(langPrefix);

  return (
    <Layout
      entityTranslations={entityTranslations}
      hideSsr={hideSsr}
      langPrefix={langPrefix}
      location={location}
      metatags={entityMetatags}
      nid={nid}
      pageType={pageType}
    >
      {currentPage > 1 && (
        <Helmet>
          {title}
          <meta
            key="case-study-robots-meta-script"
            content="follow, noindex"
            name="robots"
          />
        </Helmet>
      )}
      <Navbar menu={navbarMenu} />
      <LayoutContext.Consumer>
        {({ video, language }) => (
          <>
            <Box as="main" className="case-study-hub">
              <Box
                as="section"
                css={(theme: LucidTheme) => ({
                  color: theme.colors.white,
                  backgroundColor: theme.colors.coolGrey[90],
                  [`@media screen and (max-width: ${theme.breakpoints.tabletSmall})`]: {
                    height: 'auto'
                  }
                })}
              >
                <Box
                  css={(theme: LucidTheme) => ({
                    margin: 'auto',
                    maxWidth: 1440,
                    padding: `${theme.space[0]} ${theme.space[11]}px`,
                    display: 'flex',
                    flexWrap: 'nowrap',
                    [`@media screen and (max-width: ${theme.breakpoints.computer})`]: {
                      padding: `${theme.space[0]} ${theme.space[8]}px`
                    },
                    [`@media screen and (max-width: ${theme.breakpoints.tabletSmall})`]: {
                      flexWrap: 'wrap'
                    },
                    [`@media screen and (max-width: ${theme.breakpoints.mobile})`]: {
                      padding: `${theme.space[0]} ${theme.space[4]}px`
                    }
                  })}
                >
                  <Box
                    css={(theme: LucidTheme) => ({
                      flex: '0 1 50%',
                      position: 'relative',
                      right: '9%',
                      top: 80,
                      height: 0,
                      [`@media screen and (max-width: ${theme.breakpoints.tabletLandscape})`]: {
                        flex: '0 1 70%',
                        maxWidth: '70%',
                        height: 'auto'
                      },
                      [`@media screen and (max-width: ${theme.breakpoints.tabletSmall})`]: {
                        flex: '0 1 100%',
                        maxWidth: '100%',
                        order: 1,
                        right: 0
                      }
                    })}
                  >
                    <VideoPlayer
                      css={(theme: LucidTheme) => ({
                        boxShadow: theme.shadows[15]
                      })}
                      onClick={() =>
                        video.getVideoModal(
                          'https://player.vimeo.com/video/98592765',
                          true,
                          {
                            vimeo: {
                              playerOptions: {
                                texttrack:
                                  language.shortLangPrefix === 'pt'
                                    ? language.i18nKey
                                    : language.shortLangPrefix
                              }
                            }
                          }
                        )}
                      placeholder={() => (
                        <img
                          alt={i18n('mktg-ct-cs-video-text')}
                          src="https://d2slcw3kip6qmk.cloudfront.net/marketing/images/HUB-Pearson-Image.png"
                        />
                      )}
                      ratio="square"
                    />
                  </Box>
                  <Box
                    css={(theme: LucidTheme) => ({
                      flex: '0 1 50%',
                      marginBottom: theme.space[7],
                      [`@media only screen and (max-width: ${theme.breakpoints.tabletSmall})`]: {
                        flex: '0 1 100%',
                        marginBottom: theme.space[0]
                      }
                    })}
                  >
                    <Heading
                      color="white"
                      mb={5}
                      mt={{ mobile: 8, tablet: 10 }}
                      variant="headlineS"
                    >
                      {i18n('mktg-ct-cs-page-header')}
                    </Heading>
                    <Text
                      css={(theme: LucidTheme) => ({
                        color: theme.colors.white
                      })}
                      textStyles="callout"
                    >
                      {i18n('mktg-ct-cs-cta-header')}
                    </Text>

                    {(node.fieldHeroCta as Drupal_FieldNodeCaseStudyHubFieldHeroCta[]).map(
                      (cta, i) => {
                        const ctaEntity = cta.entity as Drupal_ParagraphCtaButton;
                        const ctaMarketoForm = ctaEntity.fieldMarketo!
                          .entity as Drupal_ParagraphMarketoForm;
                        const buttonText = ctaEntity.fieldButtonText;
                        const defaultText =
                          i === 0
                            ? i18n('mktg-ct-all-get-a-quote')
                            : i18n('mktg-ct-all-request-a-demo');
                        const { url, copy } = getMarketoLink(
                          ctaMarketoForm.fieldMarketoId
                        );
                        return ctaEntity.fieldButtonLink ? (
                          <Link
                            key={ctaEntity.fieldButtonLink}
                            asButton
                            css={blurbButtonCss}
                            primary={!ctaEntity.fieldIsSecondary}
                            secondary={!!ctaEntity.fieldIsSecondary}
                            to={ctaEntity.fieldButtonLink}
                          >
                            {buttonText || defaultText}
                          </Link>
                        ) : (
                            // eslint-disable-next-line react/jsx-indent
                            <Link
                              key={ctaMarketoForm.fieldMarketoId}
                              asButton
                              css={blurbButtonCss}
                              primary={!ctaEntity.fieldIsSecondary}
                              secondary={!!ctaEntity.fieldIsSecondary}
                              to={url}
                            >
                              {buttonText || copy}
                            </Link>
                          );
                      }
                    )}
                    {phoneNumber && (
                      <Box>
                        <a
                          css={(theme: LucidTheme) => ({
                            color: theme.colors.white,
                            '&:hover': {
                              color: theme.colors.white
                            }
                          })}
                          href={phoneNumber.link}
                        >
                          {i18n('mktg-ct-cs-phone-number', {
                            phone: phoneNumber.text
                          })}
                        </a>
                      </Box>
                    )}
                  </Box>
                </Box>
              </Box>
              <Box
                as="section"
                css={(theme: LucidTheme) => ({
                  display: 'flex',
                  flexDirection: 'row',
                  flexWrap: 'wrap',
                  justifyContent: 'space-between',
                  maxWidth: 1440,
                  margin: 'auto',
                  padding: `${theme.space[9]}px ${theme.space[0]} ${theme.space[8]}px`,
                  [`@media only screen and (max-width: ${theme.breakpoints.tabletSmall})`]: {
                    paddingBottom: theme.space[0]
                  }
                })}
              >
                <Box
                  css={(theme: LucidTheme) => ({
                    width: '100%',
                    margin: `${theme.space[0]} ${theme.space[11]}px`,
                    position: 'relative',
                    padding: `210px ${theme.space[0]} ${theme.space[7]}px`,
                    [`@media only screen and (max-width: ${theme.breakpoints.tabletLandscape})`]: {
                      paddingTop: theme.space[10]
                    },
                    [`@media only screen and (max-width: ${theme.breakpoints.tabletSmall})`]: {
                      margin: `${theme.space[0]} ${theme.space[8]}px`,
                      padding: `${theme.space[6]}px ${theme.space[0]}`
                    },
                    [`@media screen and (max-width: ${theme.breakpoints.mobile})`]: {
                      margin: `${theme.space[0]} ${theme.space[4]}px`
                    }
                  })}
                >
                  <Heading
                    as="h2"
                    css={(theme: LucidTheme) => ({
                      flex: '0 1 100%',
                      marginBottom: theme.space[6]
                    })}
                    mb={5}
                    mt={6}
                    variant="subheadS"
                  >
                    {i18n('mktg-ct-cs-studies-header')}
                  </Heading>
                  <Box
                    css={(theme: LucidTheme) => ({
                      flex: '0 1 50%',
                      width: '50%',
                      position: 'absolute',
                      top: 0,
                      right: 0,
                      borderBottom: `1px solid ${theme.colors.coolGrey[20]}`,
                      [`@media only screen and (max-width: ${theme.breakpoints.tabletLandscape})`]: {
                        right: 'auto',
                        left: 0
                      },
                      [`@media only screen and (max-width: ${theme.breakpoints.tabletSmall})`]: {
                        flex: '0 1 100%',
                        width: '100%',
                        position: 'relative',
                        marginBottom: theme.space[6]
                      },
                      '&::before': {
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: 25,
                        height: '100%',
                        backgroundImage:
                          'url("data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIyNCIgaGVpZ2h0PSIyNCIgdmlld0JveD0iMCAwIDI0IDI0Ij48cGF0aCBmaWxsPSIjM2Q0NzUyIiBkPSJNMjEuMTcyIDI0bC03LjM4Ny03LjM4N2MtMS4zODguODc0LTMuMDI0IDEuMzg3LTQuNzg1IDEuMzg3LTQuOTcxIDAtOS00LjAyOS05LTlzNC4wMjktOSA5LTkgOSA0LjAyOSA5IDljMCAxLjc2MS0uNTE0IDMuMzk4LTEuMzg3IDQuNzg1bDcuMzg3IDcuMzg3LTIuODI4IDIuODI4em0tMTIuMTcyLThjMy44NTkgMCA3LTMuMTQgNy03cy0zLjE0MS03LTctNy03IDMuMTQtNyA3IDMuMTQxIDcgNyA3eiIvPjwvc3ZnPg==")',
                        backgroundRepeat: 'no-repeat',
                        backgroundPosition: '0 7px',
                        backgroundSize: '70%'
                      }
                    })}
                  >
                    <input
                      css={(theme: LucidTheme) => ({
                        display: 'inline-block',
                        width: '100%',
                        paddingBottom: theme.space[2],
                        paddingLeft: theme.space[5],
                        background: 'transparent',
                        border: 0,
                        outline: 'none',
                        height: 40,
                        fontSize: '1rem'
                      })}
                      defaultValue={search}
                      name="search"
                      onChange={e => handleSearch(e.target.value)}
                      placeholder="Search case studies"
                      type="text"
                    />
                  </Box>
                  {allTiles.length > 10 && (
                    <Box
                      css={(theme: LucidTheme) => ({
                        flex: '0 1 100%',
                        display: 'flex',
                        flexFlow: 'row nowrap',
                        justifyContent: 'space-between',
                        [`@media only screen and (max-width: ${theme.breakpoints.tabletSmall})`]: {
                          flexWrap: 'wrap'
                        }
                      })}
                    >
                      <CaseStudySelect
                        classNamePrefix="csh-filter"
                        css={filterCss}
                        dropdownIndicator={() => {
                          return <Box>Hello</Box>;
                        }}
                        isSearchable={false}
                        onChange={(selectedOption: any) =>
                          setIndustry(selectedOption?.value)}
                        options={filterGroups.industry}
                        placeholder={i18n('mktg-ct-cs-placeholder-1')}
                        value={filterGroups.industry.find(
                          groupIndustry => groupIndustry.label === industry
                        )}
                      />
                      <CaseStudySelect
                        classNamePrefix="csh-filter"
                        css={filterCss}
                        isSearchable={false}
                        onChange={(selectedOption: any) =>
                          setSize(selectedOption.value)}
                        options={filterGroups.size}
                        placeholder={i18n('mktg-ct-cs-placeholder-2')}
                        value={filterGroups.size.find(
                          groupSize => groupSize.label === size
                        )}
                      />
                      <CaseStudySelect
                        classNamePrefix="csh-filter"
                        css={filterCss}
                        isSearchable={false}
                        onChange={(selectedOption: any) =>
                          setRole(selectedOption.value)}
                        options={filterGroups.role}
                        placeholder={i18n('mktg-ct-cs-placeholder-3')}
                        value={filterGroups.role.find(
                          groupRole => groupRole.label === role
                        )}
                      />
                    </Box>
                  )}
                </Box>
              </Box>
              <Box
                as="section"
                css={(theme: LucidTheme) => ({
                  display: 'flex',
                  flexWrap: 'wrap',
                  justifyContent: 'space-between',
                  position: 'relative',
                  margin: 'auto',
                  maxWidth: 1440,
                  padding: `${theme.space[0]} ${theme.space[11]}px`,
                  [`@media screen and (max-width: ${theme.breakpoints.tabletSmall})`]: {
                    padding: `${theme.space[0]} ${theme.space[6]}px`,
                    flex: '0 1 100%',
                    maxWidth: '100%'
                  },
                  [`@media screen and (max-width: ${theme.breakpoints.mobile})`]: {
                    padding: `${theme.space[0]} ${theme.space[4]}px`
                  }
                })}
              >
                {tiles
                  .slice(0, (currentPage - page + 1) * pageSize)
                  .map(study => (
                    <Link
                      key={study.entityUrl!.translate!.path!}
                      css={(theme: LucidTheme) => ({
                        flex: '0 1 30%',
                        maxWidth: '30%',
                        marginBottom: theme.space[7],
                        display: 'block',
                        position: 'relative',
                        [`@media screen and (max-width: ${theme.breakpoints.tabletLandscape})`]: {
                          flex: '0 1 45%',
                          maxWidth: '45%'
                        },
                        [`@media screen and (max-width: ${theme.breakpoints.mobile})`]: {
                          flex: '0 1 100%',
                          maxWidth: '100%'
                        }
                      })}
                      to={study.entityUrl!.translate!.path!}
                      underline="none"
                    >
                      <Card
                        css={{
                          height: 150
                        }}
                        variant="interactive"
                      >
                        <Card.Content css={{ padding: '0', maxHeight: '100%' }}>
                          <img
                            alt={study.title!}
                            css={{
                              maxHeight: ' 80%',
                              maxWidth: '65%',
                              width: 'auto',
                              margin: 'auto'
                            }}
                            src={`${study.fieldHubLogo || study.fieldLogo!}`}
                          />
                        </Card.Content>
                      </Card>
                    </Link>
                  ))}
                {tiles.length < 1 && (
                  <Box
                    css={(theme: LucidTheme) => ({
                      textAlign: 'center',
                      flex: '0 1 100%',
                      marginBottom: theme.space[7]
                    })}
                  >
                    <p>{i18n('mktg-ct-cs-empty-results')}</p>
                  </Box>
                )}
              </Box>
              <Box
                as="section"
                css={(theme: LucidTheme) => ({
                  display: 'flex',
                  flexDirection: 'row',
                  flexWrap: 'nowrap',
                  justifyContent: 'space-between',
                  position: 'relative',
                  margin: 'auto',
                  maxWidth: 1440,
                  padding: `${theme.space[6]}px ${theme.space[11]}px ${theme.space[8]}px`,
                  alignItems: 'center',
                  [`@media only screen and (max-width: ${theme.breakpoints.tabletSmall})`]: {
                    padding: `${theme.space[6]}px ${theme.space[8]}px ${theme.space[8]}px`
                  },
                  [`@media only screen and (max-width: ${theme.breakpoints.mobile})`]: {
                    padding: `${theme.space[6]}px ${theme.space[4]}px ${theme.space[8]}px`
                  }
                })}
              >
                <hr
                  css={(theme: LucidTheme) => ({
                    border: 0,
                    borderBottom: `2px solid ${theme.colors.coolGrey[20]}`,
                    margin: theme.space[0],
                    width: '100%'
                  })}
                />
                {hasMoreWebinars && (
                  <Link
                    css={(theme: LucidTheme) => ({
                      cursor: 'pointer',
                      paddingLeft: theme.space[2],
                      paddingRight: theme.space[0],
                      whiteSpace: 'nowrap',
                      background: 'transparent',
                      border: 0,
                      fontSize: '1rem',
                      color: theme.colors.coolGrey[70],
                      '&:visited': {
                        color: theme.colors.coolGrey[70]
                      }
                    })}
                    onClick={viewMore}
                    to={`${hubAlias}/${currentPage + 1}`}
                  >
                    {i18n('mktg-ct-cs-view-more')}
                    <Box
                      css={(theme: LucidTheme) => ({
                        display: 'inline-block',
                        fontSize: theme.fontSizes[2],
                        marginLeft: theme.space[2]
                      })}
                    >
                      <Icon type={IconType.CarotDown} />
                    </Box>
                  </Link>
                )}
              </Box>
            </Box>
            <AdditionalResources
              ctas={(node.fieldAdditionalResourcesCtas as Drupal_FieldNodeCaseStudyHubFieldAdditionalResourcesCtas[]).map(
                ({ entity }) => entity as Drupal_ParagraphCtaButton
              )}
              resources={(node.fieldAdditionalResources as Drupal_FieldNodeCaseStudyHubFieldAdditionalResources[]).map(
                ({ entity }) => entity as Drupal_ParagraphAdditionalResources
              )}
            />
          </>
        )}
      </LayoutContext.Consumer>
      <Footer entityTranslations={entityTranslations} menu={footerMenu} />
    </Layout>
  );
};

export default CaseStudyHub;

export const query = graphql`
  query(
    $nid: String!
    $LanguageId: Drupal_LanguageId!
    $i18nKey: String!
    $langPrefix: [String!]
  ) {
    drupal {
      nodeQuery(
        limit: 1000
        filter: {
          conditions: [
            { field: "type", value: ["case_study"], operator: IN }
            { field: "field_is_private", value: "0", operator: EQUAL }
            { field: "status", value: "1", operator: EQUAL }
            { field: "langcode", value: $langPrefix, operator: EQUAL }
          ]
        }
        sort: [
          { field: "field_weight", direction: ASC }
          { field: "field_company_name", direction: ASC }
        ]
      ) {
        entities {
          ... on Drupal_NodeCaseStudy {
            entityUrl {
              translate(language: $LanguageId) {
                path
              }
            }
            fieldCompanyName
            fieldLogo
            fieldHubLogo
            fieldRole {
              entity {
                name
              }
            }
            fieldSize {
              entity {
                name
              }
            }
            fieldIndustry {
              entity {
                name
              }
            }
            fieldSearchTerms {
              entity {
                name
              }
            }
          }
        }
      }
      taxonomyTermQuery(
        limit: 1000
        filter: {
          conditions: [
            { field: "vid", operator: IN, value: ["role", "industry", "size"] }
          ]
        }
      ) {
        entities {
          ... on Drupal_TaxonomyTerm {
            entityBundle
            name
            entityTranslation(language: $LanguageId) {
              entityLabel
            }
          }
        }
      }
      nodeById(id: $nid, language: $LanguageId) {
        ... on Drupal_NodeCaseStudyHub {
          ...entityFields
          fieldHeroCta {
            entity {
              ... on Drupal_ParagraphCtaButton {
                fieldButtonText
                fieldButtonLink
                fieldIsSecondary
                fieldMarketo {
                  entity {
                    ... on Drupal_ParagraphMarketoForm {
                      fieldMarketoId
                    }
                  }
                }
              }
            }
          }
          fieldAdditionalResources {
            entity {
              ... on Drupal_ParagraphAdditionalResources {
                fieldResourceUrl
                fieldResourceText
              }
            }
          }
          fieldAdditionalResourcesCtas {
            entity {
              ... on Drupal_ParagraphCtaButton {
                fieldButtonText
                fieldButtonLink
                fieldIsSecondary
                fieldMarketo {
                  entity {
                    ... on Drupal_ParagraphMarketoForm {
                      fieldMarketoId
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
    i18nData: allFile(
      filter: {
        sourceInstanceName: { eq: "i18n" }
        name: { in: [$i18nKey, "en"] }
      }
    ) {
      edges {
        node {
          name
          childSrcJson {
            mktg_ct_cs_video_text
            mktg_ct_cs_page_header
            mktg_ct_cs_cta_header
            mktg_ct_cs_cta_primary
            mktg_ct_cs_cta_secondary
            mktg_ct_all_get_a_quote
            mktg_ct_all_request_a_demo
            mktg_ct_cs_phone_number
            mktg_ct_cs_studies_header
            mktg_ct_all_or_call
            mktg_ct_cs_placeholder_1
            mktg_ct_cs_placeholder_2
            mktg_ct_cs_placeholder_3
            mktg_ct_cs_empty_results
            mktg_ct_cs_view_more
          }
        }
      }
    }
  }
`;
